import React, { useEffect, useMemo, useState } from 'react';
import {
  Card,
  Table,
  Button,
  Modal,
  Form,
  Input,
  Select,
  Switch,
  Space,
  Typography,
  message,
  Popconfirm,
  Tag,
  Grid,
} from 'antd';
import {
  LinkOutlined,
  PlusOutlined,
  ReloadOutlined,
  DeleteOutlined,
  EditOutlined,
  MailOutlined,
  CopyOutlined,
} from '@ant-design/icons';
import { autofacturaConfigsService, companiesService, receiversService } from '../../services/api';
import SATCatalogSearch from '../../components/Common/SATCatalogSearch';

const { Title, Text } = Typography;
const { Option } = Select;
const { useBreakpoint } = Grid;

const AutofacturaConfigs = () => {
  const screens = useBreakpoint();
  const [loading, setLoading] = useState(false);
  const [configs, setConfigs] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [receivers, setReceivers] = useState([]);
  const [modalVisible, setModalVisible] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form] = Form.useForm();

  const loadData = async () => {
    setLoading(true);
    try {
      const [cfgResp, compResp, recvResp] = await Promise.all([
        autofacturaConfigsService.getConfigs(),
        companiesService.getCompanies(),
        receiversService.getReceivers(''),
      ]);

      setConfigs(cfgResp?.data?.configs || []);
      setCompanies(compResp?.data?.companies || []);
      setReceivers(recvResp?.data || []);
    } catch (e) {
      message.error('Error al cargar configuraciones de autoportal');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  const openModal = (record = null) => {
    setEditing(record);
    setModalVisible(true);
    if (record) {
      form.setFieldsValue({
        operador_id: record.operador_id,
        permisionario_id: record.permisionario_id,
        concepto: record.concepto,
        clave_prod_serv: record.clave_prod_serv || '01010101',
        clave_unidad: record.clave_unidad || 'ACT',
        uso_cfdi: record.uso_cfdi || 'G03',
        activo: Boolean(record.activo),
      });
    } else {
      form.resetFields();
      form.setFieldsValue({
        activo: true,
        clave_prod_serv: '01010101',
        clave_unidad: 'ACT',
        uso_cfdi: 'G03',
      });
    }
  };

  const closeModal = () => {
    setModalVisible(false);
    setEditing(null);
    form.resetFields();
  };

  const buildPublicLink = (token) => {
    const origin = window.location.origin;
    return `${origin}/autofactura/${token}`;
  };

  const copyLink = async (token) => {
    const link = buildPublicLink(token);
    try {
      await navigator.clipboard.writeText(link);
      message.success('Link copiado');
    } catch (e) {
      message.error('No se pudo copiar el link');
    }
  };

  const handleSubmit = async (values) => {
    setLoading(true);
    try {
      const payload = {
        operador_id: values.operador_id,
        permisionario_id: values.permisionario_id,
        concepto: values.concepto,
        clave_prod_serv: values.clave_prod_serv,
        clave_unidad: values.clave_unidad,
        uso_cfdi: values.uso_cfdi,
        activo: Boolean(values.activo),
      };

      if (editing?.id) {
        await autofacturaConfigsService.updateConfig(editing.id, payload);
        message.success('Configuración actualizada');
      } else {
        await autofacturaConfigsService.createConfig(payload);
        message.success('Configuración creada');
      }

      closeModal();
      await loadData();
    } catch (e) {
      message.error(e?.response?.data?.error || 'Error al guardar configuración');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    setLoading(true);
    try {
      await autofacturaConfigsService.deleteConfig(id);
      message.success('Configuración eliminada');
      await loadData();
    } catch (e) {
      message.error(e?.response?.data?.error || 'Error al eliminar configuración');
    } finally {
      setLoading(false);
    }
  };

  const handleRegenerateToken = async (id) => {
    setLoading(true);
    try {
      await autofacturaConfigsService.regenerateToken(id);
      message.success('Token regenerado');
      await loadData();
    } catch (e) {
      message.error(e?.response?.data?.error || 'Error al regenerar token');
    } finally {
      setLoading(false);
    }
  };

  const handleSendLink = async (token) => {
    setLoading(true);
    try {
      const resp = await autofacturaConfigsService.sendLinkEmail(token);
      const sentTo = resp?.data?.sent_to;
      message.success(sentTo ? `Link enviado a ${sentTo}` : 'Link enviado');
    } catch (e) {
      message.error(e?.response?.data?.error || 'Error al enviar el link');
    } finally {
      setLoading(false);
    }
  };

  const companyMap = useMemo(() => {
    const m = new Map();
    (companies || []).forEach((c) => m.set(c.id, c));
    return m;
  }, [companies]);

  const receiverMap = useMemo(() => {
    const m = new Map();
    (receivers || []).forEach((r) => m.set(r.id, r));
    return m;
  }, [receivers]);

  const columns = [
    {
      title: 'Empresa',
      key: 'company',
      render: (_, record) => {
        const c = record.company || companyMap.get(record.operador_id);
        return (
          <div>
            <div style={{ fontWeight: 600 }}>{c?.tax_name || record.operador_id}</div>
            <div style={{ fontSize: 12, color: '#8c8c8c' }}>{c?.tax_id || ''}</div>
          </div>
        );
      },
    },
    {
      title: 'Receptor',
      key: 'receiver',
      render: (_, record) => {
        const r = record.receiver || receiverMap.get(record.permisionario_id);
        return (
          <div>
            <div style={{ fontWeight: 600 }}>{r?.tax_name || record.permisionario_id}</div>
            <div style={{ fontSize: 12, color: '#8c8c8c' }}>{r?.email || ''}</div>
          </div>
        );
      },
    },
    {
      title: 'Concepto',
      dataIndex: 'concepto',
      key: 'concepto',
      ellipsis: true,
    },
    {
      title: 'Estado',
      dataIndex: 'activo',
      key: 'activo',
      width: 100,
      render: (v) => (
        <Tag color={v ? 'green' : 'red'}>{v ? 'Activo' : 'Inactivo'}</Tag>
      ),
    },
    {
      title: 'Link',
      key: 'link',
      width: 120,
      render: (_, record) => (
        <Button
          icon={<CopyOutlined />}
          onClick={() => copyLink(record.token)}
        >
          Copiar
        </Button>
      ),
    },
    {
      title: 'Acciones',
      key: 'actions',
      fixed: screens.lg ? 'right' : undefined,
      width: screens.lg ? 380 : 280,
      render: (_, record) => (
        <Space wrap>
          <Button icon={<EditOutlined />} onClick={() => openModal(record)}>
            Editar
          </Button>
          <Popconfirm
            title="¿Regenerar el token?"
            description="El link anterior dejará de funcionar."
            okText="Regenerar"
            cancelText="Cancelar"
            onConfirm={() => handleRegenerateToken(record.id)}
          >
            <Button icon={<ReloadOutlined />}>Token</Button>
          </Popconfirm>
          <Button icon={<MailOutlined />} onClick={() => handleSendLink(record.token)}>
            Enviar link
          </Button>
          <Popconfirm
            title="¿Eliminar configuración?"
            okText="Eliminar"
            cancelText="Cancelar"
            onConfirm={() => handleDelete(record.id)}
          >
            <Button danger icon={<DeleteOutlined />}>
              Eliminar
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div style={{ padding: screens.xs ? '12px' : '24px' }}>
      <Card>
        <div
          style={{
            display: 'flex',
            flexDirection: screens.md ? 'row' : 'column',
            justifyContent: 'space-between',
            alignItems: screens.md ? 'center' : 'flex-start',
            gap: '12px',
            marginBottom: 16,
          }}
        >
          <div>
            <Title level={2} style={{ margin: 0 }}>
              <LinkOutlined style={{ marginRight: 8 }} />
              Autoportal de facturación
            </Title>
            <Text type="secondary">
              Administra los links para generar facturas con 1 click
            </Text>
          </div>
          <Button type="primary" icon={<PlusOutlined />} onClick={() => openModal(null)} block={!screens.md}>
            Nuevo link
          </Button>
        </div>

        <Table
          rowKey="id"
          columns={columns}
          dataSource={configs}
          loading={loading}
          scroll={{ x: 'max-content' }}
          pagination={{ pageSize: 10 }}
        />
      </Card>

      <Modal
        title={editing ? 'Editar link' : 'Nuevo link'}
        open={modalVisible}
        onCancel={closeModal}
        footer={null}
        width={screens.xs ? '100%' : 620}
        destroyOnClose
      >
        <Form form={form} layout="vertical" onFinish={handleSubmit}>
          <Form.Item
            name="operador_id"
            label="Empresa (operador)"
            rules={[{ required: true, message: 'Selecciona una empresa' }]}
          >
            <Select showSearch optionFilterProp="children" placeholder="Selecciona empresa">
              {(companies || []).map((c) => (
                <Option key={c.id} value={c.id}>
                  {c.tax_name} ({c.tax_id})
                </Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item
            name="permisionario_id"
            label="Receptor (permisionario)"
            rules={[{ required: true, message: 'Selecciona un receptor' }]}
          >
            <Select showSearch optionFilterProp="children" placeholder="Selecciona receptor">
              {(receivers || []).map((r) => (
                <Option key={r.id} value={r.id}>
                  {r.tax_name} ({r.tax_id})
                </Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item
            name="concepto"
            label="Nombre del concepto"
            rules={[{ required: true, message: 'Ingresa el concepto' }]}
          >
            <Input placeholder="Concepto que aparecerá en la factura" />
          </Form.Item>

          <Form.Item
            name="clave_prod_serv"
            label="ClaveProdServ"
            rules={[
              { required: true, message: 'Ingresa la clave prod/serv' },
              { pattern: /^\d{8}$/, message: 'Debe tener 8 dígitos' },
            ]}
          >
            <SATCatalogSearch type="products" placeholder="Busca por clave o descripción" />
          </Form.Item>

          <Form.Item
            name="clave_unidad"
            label="ClaveUnidad"
            rules={[
              { required: true, message: 'Ingresa la clave unidad' },
              { pattern: /^[A-Z0-9]{3}$/, message: 'Debe tener 3 caracteres' },
            ]}
          >
            <SATCatalogSearch type="units" placeholder="Busca por clave o descripción" />
          </Form.Item>

          <Form.Item
            name="uso_cfdi"
            label="Uso CFDI"
            rules={[
              { required: true, message: 'Selecciona el uso CFDI' },
              { pattern: /^[A-Z0-9]{3}$/, message: 'Código inválido' },
            ]}
          >
            <Select showSearch placeholder="Selecciona uso CFDI">
              <Option value="G01">G01 - Adquisición de mercancías</Option>
              <Option value="G02">G02 - Devoluciones, descuentos o bonificaciones</Option>
              <Option value="G03">G03 - Gastos en general</Option>
              <Option value="I01">I01 - Construcciones</Option>
              <Option value="I02">I02 - Mobilario y equipo de oficina por inversiones</Option>
              <Option value="I03">I03 - Equipo de transporte</Option>
              <Option value="I04">I04 - Equipo de computo y accesorios</Option>
              <Option value="I05">I05 - Dados, troqueles, moldes, matrices y herramental</Option>
              <Option value="I06">I06 - Comunicaciones telefónicas</Option>
              <Option value="I07">I07 - Comunicaciones satelitales</Option>
              <Option value="I08">I08 - Otra maquinaria y equipo</Option>
              <Option value="P01">P01 - Por definir</Option>
            </Select>
          </Form.Item>

          <Form.Item name="activo" label="Activo" valuePropName="checked">
            <Switch />
          </Form.Item>

          <Form.Item style={{ textAlign: 'right', marginBottom: 0 }}>
            <Space>
              <Button onClick={closeModal}>Cancelar</Button>
              <Button type="primary" htmlType="submit" loading={loading}>
                Guardar
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default AutofacturaConfigs;
